import { cache } from 'tm3-core/cache';
import { z } from 'zod';

export const zodValidationSchema: Record<string, z.ZodTypeAny> = {
  ReferralPortalAppointmentRequest: z.object({
    serviceIds: z.array(z.lazy(() => zodValidationSchema.Guid)).min(1, 'Required')
  }),
  ReferralPortalLocationRequest: z.object({ serviceId: z.string().nullable() }),
  ReferralPortalReferralRequest: z.object({
    id: z
      .number()
      .int()
      .refine((val) => val !== 0, 'Required')
  }),
  ReferralPortalReserveAppointmentRequest: z.object({
    startDateTime: z.any(),
    endDateTime: z.any(),
    serviceId: z.string()
  }),
  ReferralPortalTemplateForServiceRequest: z.object({
    serviceId: z
      .string()
      .nullable()
      .refine((val) => val !== null, 'Required')
  }),
  DiscountCodeDto: z.object({
    code: z
      .string({ required_error: 'A discount code is required' })
      .min(1, 'A discount code is required'),
    value: z.number().refine((val) => val !== 0, 'A discount value is required')
  }),
  MedicationRequestItem: z.object({
    vpid: z.string().min(1, 'Required'),
    description: z.string().min(1, 'Required'),
    quantity: z.string().min(1, 'Required'),
    directions: z.string().min(1, 'Required')
  }),
  CreatePrescriptionRequest: z.object({
    action: z
      .enum([
        'draft',
        'issueForCollection',
        'issueToContact',
        'issueForDelivery',
        'issueOnly',
        'printPrescription'
      ])
      .refine((val) => val !== null, 'Required'),
    medications: z
      .array(z.lazy(() => zodValidationSchema.MedicationRequestItem))
      .min(1, 'Required'),
    securePin: z.string().min(6, 'Requires 6 character(s)')
  }),
  ChangePINRequest: z.object({
    password: z.string().min(1, 'Required'),
    pin: z.string().min(6, 'Requires 6 character(s)')
  }),
  ChangeSignatureRequest: z.object({ signature: z.string().min(1, 'Required') }),
  TreatmentAuthSave: z.object({
    authCode: z
      .string({ required_error: 'An Authorisation Code is required' })
      .min(1, 'An Authorisation Code is required')
  }),
  CustomerSaveRequest: z
    .object({
      forename: z.string().min(1, 'Requires 1 character(s)').max(40),
      surname: z.string().min(1, 'Requires 1 character(s)').max(50),
      homeTelephone: z.string().optional(),
      workTelephone: z.string().optional(),
      mobileTelephone: z.string().optional(),
      dateOfBirth: z
        .any()
        .nullable()
        .refine(
          (val) => {
            if (cache?.config?.['MAKEDOBREQUIRED']?.toUpperCase() === 'TRUE') return !!val;
            return true;
          },
          { message: 'Required' }
        ),
      practitionerId: z
        .number()
        .int()
        .refine(
          (val) => {
            if (cache?.config?.['REQUIREPRACTITIONERTOBELINKED']?.toUpperCase() === 'TRUE')
              return !!val;
            return true;
          },
          { message: 'Required' }
        ),
      smsEnabled: z.boolean().nullable().optional()
    })
    .refine(
      (data) => {
        if (data.smsEnabled) {
          return !!data.mobileTelephone;
        }
        return true;
      },
      { message: 'Required when SMSEnabled is enabled', path: ['mobileTelephone'] }
    )
    .refine(
      (data) =>
        (data.homeTelephone !== undefined &&
          data.homeTelephone !== null &&
          String(data.homeTelephone).trim() !== '') ||
        (data.workTelephone !== undefined &&
          data.workTelephone !== null &&
          String(data.workTelephone).trim() !== '') ||
        (data.mobileTelephone !== undefined &&
          data.mobileTelephone !== null &&
          String(data.mobileTelephone).trim() !== ''),
      { message: 'At least one phone number is required', path: ['phoneNumbers'] }
    ),
  ExtendedNotesSave: z.object({
    type: z.string().min(1, 'Required'),
    title: z.string().min(1, 'Required')
  }),
  InvoiceSaveRequest: z.object({}),
  PinUnlockRequest: z.object({ pin: z.string().min(6, 'Requires 6 character(s)') }),
  PostcodeLookupRequest: z.object({
    postcode: z
      .string({ required_error: 'A postcode is required' })
      .min(1, 'A postcode is required')
      .regex(new RegExp('^[a-zA-Z0-9 -]+$'), 'A valid postcode is required')
  }),
  StockDeliveryRequest: z
    .object({
      stockId: z
        .number()
        .int()
        .refine((val) => val !== 0, 'Required')
        .refine((val) => val > 0, 'Required'),
      locationId: z
        .number()
        .int()
        .refine((val) => val !== 0, 'Required')
        .refine((val) => val > 0, 'Required'),
      quantity: z
        .number()
        .refine((val) => val !== 0, 'Required')
        .refine((val) => val > 0, 'Required'),
      batchCode: z.string().optional(),
      reason: z.string().max(1024).optional(),
      batchTraceable: z.boolean().optional()
    })
    .refine(
      (data) => {
        if (data.batchTraceable) {
          return !!data.batchCode;
        }
        return true;
      },
      { message: 'Required when BatchTraceable is enabled', path: ['batchCode'] }
    ),
  StockWriteOffRequest: z.object({
    levelId: z
      .number()
      .int()
      .refine((val) => val !== 0, 'Required')
      .refine((val) => val > 0, 'Required'),
    locationId: z
      .number()
      .int()
      .refine((val) => val !== 0, 'Required')
      .refine((val) => val > 0, 'Required'),
    quantity: z
      .number()
      .refine((val) => val !== 0, 'Required')
      .refine((val) => val > 0, 'Required'),
    reason: z.string().max(1024).optional()
  }),
  StockTransferReq: z.object({
    to: z
      .number()
      .int()
      .refine((val) => val !== 0, 'Required')
      .refine((val) => val > 0, 'Required'),
    quantity: z
      .number()
      .refine((val) => val !== 0, 'Required')
      .refine((val) => val > 0, 'Required')
  }),
  StockInventoryAdjustmentRequest: z
    .object({
      stockId: z
        .number()
        .int()
        .refine((val) => val !== 0, 'Required')
        .refine((val) => val > 0, 'Required'),
      locationId: z
        .number()
        .int()
        .refine((val) => val !== 0, 'Required')
        .refine((val) => val > 0, 'Required'),
      quantity: z
        .number()
        .refine((val) => val !== 0, 'Required')
        .refine((val) => val > 0, 'Required'),
      batchCode: z.string().optional(),
      reason: z.string().max(1024).optional(),
      batchTraceable: z.boolean().optional()
    })
    .refine(
      (data) => {
        if (data.batchTraceable) {
          return !!data.batchCode;
        }
        return true;
      },
      { message: 'Required when BatchTraceable is enabled', path: ['batchCode'] }
    ),
  TaskChecklistItemCreateRequest: z.object({ text: z.string().min(1, 'Required') }),
  TaskChecklistTemplateSaveRequest: z.object({
    name: z.string().min(1, 'Required'),
    taskDescription: z.string().min(1, 'Required')
  }),
  TaskChecklistTemplateStepsSaveRequest: z.object({
    steps: z.array(z.lazy(() => zodValidationSchema.TaskChecklistTemplateStepDto))
  }),
  TaskChecklistAutoRuleSaveRequest: z.object({
    name: z.string().min(1, 'Required'),
    assignmentUserId: z.number().int().nullable(),
    assignmentTeamId: z.string().nullable(),
    dueDateMode: z.string().optional(),
    dueDaysOffset: z.number().int().nullable(),
    templateIds: z.array(z.lazy(() => zodValidationSchema.Int32))
  }),
  TaskChecklistRecurringRuleSaveRequest: z.object({
    name: z.string({ required_error: 'Task name is required.' }).min(1, 'Task name is required.'),
    intervalType: z.string(),
    weeklyDayOfWeek: z.number().int().nullable(),
    monthlyMode: z.string(),
    monthlyDayOfMonth: z.number().int().nullable(),
    userIds: z.array(z.lazy(() => zodValidationSchema.Int32))
  }),
  TaskChecklistTemplateApplyRequest: z.object({
    targetType: z.string().min(1, 'Required'),
    targetId: z
      .number()
      .int()
      .refine((val) => val > 0, 'TargetId must be a valid entity ID.')
  }),
  TaskSaveRequest: z
    .object({
      dueDate: z.any().refine((val) => val !== null, 'Required'),
      description: z.string().min(1, 'Required'),
      userId: z.number().int().nullable().optional(),
      teamId: z.string().nullable().optional(),
      recur: z.boolean().optional(),
      recurFreq: z
        .enum(['Daily', 'WorkingDays', 'Weekly', 'Fortnightly', 'Monthly', 'Yearly'])
        .optional(),
      recurDate: z.any().optional()
    })
    .refine(
      (data) => {
        if (data.recur) {
          return !!data.recurFreq;
        }
        return true;
      },
      { message: 'Required when Recur is enabled', path: ['recurFreq'] }
    )
    .refine(
      (data) => {
        if (data.recur) {
          return !!data.recurDate;
        }
        return true;
      },
      { message: 'Required when Recur is enabled', path: ['recurDate'] }
    )
    .refine(
      (data) =>
        (data.userId !== undefined && data.userId !== null && String(data.userId).trim() !== '') ||
        (data.teamId !== undefined && data.teamId !== null && String(data.teamId).trim() !== ''),
      { message: 'Tasks must be assigned to a user or a team.', path: ['assignee'] }
    ),
  StripeTerminalPairRequest: z.object({
    locationId: z
      .number()
      .int()
      .refine((val) => val !== 0, 'Required'),
    stripeLocationId: z.string().min(1, 'Required'),
    deviceName: z.string().min(1, 'Required')
  }),
  SaveReferralPortalLoginMessageRequest: z.object({
    title: z
      .string({ required_error: 'Title is required' })
      .min(1, 'Title is required')
      .max(150, 'Title maximum length is 150 characters'),
    content: z
      .string({ required_error: 'Message content is required' })
      .min(1, 'Message content is required')
      .max(32768, 'Message content maximum length is 32768 characters')
  }),
  NationwideUploadReschedule: z.object({}),
  CreateBillingEntity: z.object({ name: z.string().min(1, 'Required') }),
  UpdateBillingEntity: z.object({
    id: z
      .number()
      .int()
      .refine((val) => val > 0, 'Required'),
    name: z.string().min(1, 'Required')
  }),
  EquipmentSaveRequest: z.object({
    name: z.string().min(4, 'Requires 4 character(s)').max(255),
    model: z.string().max(255).optional(),
    assetTag: z.string().max(255).optional()
  }),
  QueryReportRequest: z.object({
    comments: z
      .string({ required_error: 'Comments are required' })
      .min(1, 'Comments are required')
      .max(255, 'Comments character limit is 255 characters')
  }),
  RejectReportRequest: z.object({
    comments: z
      .string({ required_error: 'Comments are required' })
      .min(1, 'Comments are required')
      .max(255, 'Comments character limit is 255 characters')
  }),
  ReferralPortalCancelAppointment: z.object({
    appointmentId: z
      .number()
      .int()
      .nullable()
      .refine((val) => val !== null, 'Required'),
    workflowId: z
      .number()
      .int()
      .nullable()
      .refine((val) => val !== null, 'Required')
  }),
  ReferralPortalAddAppointmentToReferralRequest: z.object({
    workflowId: z
      .number()
      .int()
      .nullable()
      .refine((val) => val !== null, 'Required'),
    appointmentId: z
      .number()
      .int()
      .nullable()
      .refine((val) => val !== null, 'Required')
  }),
  ReferralPortalAllowedServicesRequest: z.object({
    id: z
      .number()
      .int()
      .nullable()
      .refine((val) => val !== null, 'Required')
  }),
  ReferralPortalFieldsForServiceRequest: z.object({
    serviceId: z
      .string()
      .nullable()
      .refine((val) => val !== null, 'Required')
  }),
  CaseUnlockRequest: z.object({ reason: z.string().min(1, 'Required').max(1024) }),
  CaseUndischargeRequest: z.object({ reason: z.string().min(1, 'Required').max(1024) }),
  CloneWorkSheetRequest: z.object({
    id: z
      .number()
      .int()
      .refine((val) => val !== 0, 'A WorkSheet Id is required')
  }),
  CreateVehicleRequest: z.object({
    registrationNumber: z
      .string({ required_error: 'A Registration Number is required' })
      .min(1, 'A Registration Number is required')
  }),
  CreateWorkSheetRequest: z.object({ customer: z.string() }),
  SaveWorkSheetRequest: z.object({ customer: z.string() }),
  UpdateVehicleOwnerRequest: z.object({
    customerId: z
      .number()
      .int()
      .refine((val) => val !== 0, 'Required'),
    vehicleId: z
      .number()
      .int()
      .refine((val) => val !== 0, 'Required')
  }),
  Vehicle: z.object({
    registrationNumber: z
      .string({ required_error: 'A Registration Number is required' })
      .min(1, 'A Registration Number is required')
  }),
  ReferralPortalCancelAppointment: z.object({
    appointmentId: z
      .number()
      .int()
      .nullable()
      .refine((val) => val !== null, 'Required'),
    workflowId: z
      .number()
      .int()
      .nullable()
      .refine((val) => val !== null, 'Required')
  }),
  ReferralPortalAddAppointmentToReferralRequest: z.object({
    workflowId: z
      .number()
      .int()
      .nullable()
      .refine((val) => val !== null, 'Required'),
    appointmentId: z
      .number()
      .int()
      .nullable()
      .refine((val) => val !== null, 'Required')
  }),
  ReferralPortalAllowedServicesRequest: z.object({
    id: z
      .number()
      .int()
      .nullable()
      .refine((val) => val !== null, 'Required')
  }),
  ReferralPortalFieldsForServiceRequest: z.object({
    serviceId: z
      .string()
      .nullable()
      .refine((val) => val !== null, 'Required')
  })
};

export default zodValidationSchema;
